import React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles, FormView, IActionMenuItem } from 'rewire-ui';
import { AccountOffOutline as AccountOffIcon } from 'mdi-material-ui';
import { Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import AddUdfIcon from '@material-ui/icons/PlaylistAdd';
import EmployeeDialog from './EmployeeDialog';
import AddOccupationDialog from './AddOccupationDialog';
import UpdateJobEffectiveRangeDialog from './UpdateJobEffectiveRangeDialog';
import GeneratorCalendar from './GeneratorCalendar';
import GeneratorDialog from './GeneratorDialog';
import Loader from '../ui/Loader';
import InfoPanelHeader from '../ui/InfoPanelHeader';
import InfoPanelContent from '../ui/InfoPanelContent';
import InfoPanelFooter from '../ui/InfoPanelFooter';
import infoPanelJobViewModel from './InfoPanelJobViewModel';
import UdfEditor from '../settings/UdfEditor';
import scheduleModel from './ScheduleModel';
import Typography from '@material-ui/core/Typography';

const styles = (_theme: Theme) => ({
  fieldContainer: {
    width: '100%',
    marginBottom: '15px',
    '& > div': {
      display: 'flex',
    },
  },
  dateFieldsContainer: {
    width: '100%',
    display: 'flex',
    marginBottom: '15px',
    '& > div': {
      display: 'flex',
      flex: '1',
      '&:first-child': {
        marginRight: '20px',
      },
    },
  },
  calendarContainer: {
    margin: '0px 0px 15px 0px',
  },
  calendar: {
    height: '320px',
  },
  calendarEvent: {
    '& > div > div': {
      padding: '2px',
    },
  },
});

interface IInfoPanelJobViewProps {
  classes?: any;
}

type InfoPanelJobViewProps = WithStyle<ReturnType<typeof styles>, IInfoPanelJobViewProps>;

class InfoPanelJobView extends React.Component<InfoPanelJobViewProps> {
  optionsActionMenuItems: IActionMenuItem[];
  multiSelectActionMenuItems: IActionMenuItem[];

  constructor(props: InfoPanelJobViewProps) {
    super(props);
    this.optionsActionMenuItems = [
      {
        name: 'updateJobEffectiveRange',
        title: 'Update Job Effective Range',
        icon: AccountOffIcon,
        onClick: this.handleUpdateJobEffectiveRangeClick,
        closeOnClick: true,
        disabled: () => !infoPanelJobViewModel.canEditJobs || !infoPanelJobViewModel.jobId || infoPanelJobViewModel.hasChanges(),
      },
      {
        name: 'duplicateJob',
        title: 'Duplicate Job',
        icon: FileCopyIcon,
        onClick: this.handleDuplicateJobClick,
        closeOnClick: true,
        disabled: () => !infoPanelJobViewModel.canAddJobs || !infoPanelJobViewModel.jobId || infoPanelJobViewModel.hasChanges(),
      },
      {
        name: 'deleteJob',
        title: 'Delete Job',
        icon: DeleteIcon,
        onClick: this.handleDeleteJobClick,
        closeOnClick: true,
        disabled: () => !infoPanelJobViewModel.canDeleteJobs,
      },
      {
        name: 'udfEditor',
        title: 'User Defined Fields',
        icon: AddUdfIcon,
        onClick: () => infoPanelJobViewModel.openUdfEditor(),
        closeOnClick: true,
        disabled: () => infoPanelJobViewModel.hasChanges(),
      },
      // {name: 'archiveJob',              title: 'Archive Job',                icon: ArchiveIcon,    onClick: this.handleArchiveJobClick,              closeOnClick: true, disabled: () => !infoPanelJobViewModel.canEditJobs || !infoPanelJobViewModel.jobId},
      // {name: 'unarchiveJob',            title: 'Unarchive Job',              icon: UnarchiveIcon,  onClick: this.handleUnarchiveJobClick,            closeOnClick: true, disabled: () => !infoPanelJobViewModel.canEditJobs || !infoPanelJobViewModel.jobId},
    ];

    this.multiSelectActionMenuItems = [
      {
        name: 'duplicateJob',
        title: 'Duplicate Jobs',
        icon: FileCopyIcon,
        onClick: infoPanelJobViewModel.showDuplicateJobsDialog,
        closeOnClick: true,
        disabled: () => !infoPanelJobViewModel.hasJobs() || infoPanelJobViewModel.hasChanges(),
      },
      {
        name: 'deleteJob',
        title: 'Delete Jobs',
        icon: DeleteIcon,
        onClick: infoPanelJobViewModel.deleteJobs,
        closeOnClick: true,
        disabled: () => !infoPanelJobViewModel.canDeleteJobs || !infoPanelJobViewModel.hasJobs(),
      },
      {
        name: 'udfEditor',
        title: 'User Defined Fields',
        icon: AddUdfIcon,
        onClick: () => infoPanelJobViewModel.openUdfEditor(),
        closeOnClick: true,
        disabled: () => infoPanelJobViewModel.hasChanges(),
      },
    ];
  }

  renderUdfs() {
    let index = infoPanelJobViewModel.form.fields.findIndex((x) => x.name === 'cycleOffset') + 1;
    const udfs: any[] = [];
    for (; index < infoPanelJobViewModel.form.fields.length; index++) {
      const Editor = infoPanelJobViewModel.form.fields[index].Editor;
      udfs.push(<Editor />);
    }
    return udfs;
  }

  componentWillUnmount() {
    infoPanelJobViewModel.reset();
  }

  handleUpdateJobEffectiveRangeClick = () => {
    infoPanelJobViewModel.showUpdateJobEffectiveRangeDialog();
  };

  handleDuplicateJobClick = () => {
    infoPanelJobViewModel.showDuplicateJobDialog();
  };

  handleDeleteJobClick = () => {
    infoPanelJobViewModel.deleteJob();
  };

  handleArchiveJobClick = () => {
    infoPanelJobViewModel.archiveJob();
  };

  handleUnarchiveJobClick = () => {
    infoPanelJobViewModel.unarchiveJob();
  };

  renderContent = React.memo((): JSX.Element => {
    const { classes } = this.props;

    return (
      <Observe
        render={() => (
          <Loader loading={infoPanelJobViewModel.isLoading}>
            <InfoPanelContent>
              <Observe
                render={() => (
                  <>
                    <FormView form={infoPanelJobViewModel.form} onSubmit={infoPanelJobViewModel.actionFn('save')}>
                      <div className={classes.dateFieldsContainer}>
                        <infoPanelJobViewModel.form.field.area.Editor />
                        <infoPanelJobViewModel.form.field.group.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.occupation.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.autoCreateVacancyForAbsenceReasons.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.removeOverlappingAssignments.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.employee.Editor />
                      </div>
                      <div className={classes.dateFieldsContainer}>
                        <infoPanelJobViewModel.form.field.effectiveStart.Editor />
                        <infoPanelJobViewModel.form.field.effectiveEnd.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.generator.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <infoPanelJobViewModel.form.field.cycleOffset.Editor />
                      </div>
                      <div className={classes.fieldContainer}>
                        <Observe>{() => <>{...this.renderUdfs()}</>}</Observe>
                      </div>
                    </FormView>
                    <div className={classes.calendarContainer}>
                      <Observe
                        render={() => (
                          <GeneratorCalendar
                            model={infoPanelJobViewModel.generatorCalendarModel}
                            classes={{ calendar: classes.calendar, customEvent: classes.calendarEvent }}
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              />
            </InfoPanelContent>
          </Loader>
        )}
      />
    );
  });

  singleSelectView = (
    <Observe
      render={() => {
        return (
          <>
            <InfoPanelHeader
              title="Job Details"
              optionsMenuTitle="Job Options"
              optionsMenuTooltip="Job Options"
              optionsItems={this.optionsActionMenuItems}
            />
            <this.renderContent />
            <Observe
              render={() => (
                <InfoPanelFooter isDisabled={infoPanelJobViewModel.isDisabled} actions={infoPanelJobViewModel.actions} />
              )}
            />

            <UpdateJobEffectiveRangeDialog />
            <EmployeeDialog />
            <GeneratorDialog />
            <AddOccupationDialog />
            <UdfEditor udfEditor={infoPanelJobViewModel.udfEditor} />
          </>
        );
      }}
    />
  );

  multiSelectView = (
    <Observe
      render={() => {
        return (
          <>
            <InfoPanelHeader
              title="Multiple Jobs Selected"
              optionsMenuTitle="Job Options"
              optionsMenuTooltip="Job Options"
              optionsItems={this.multiSelectActionMenuItems}
            />
            <Typography style={{ paddingLeft: '16px' }}>Multiple Jobs Selected</Typography>
            <UdfEditor udfEditor={infoPanelJobViewModel.udfEditor} />
          </>
        );
      }}
    />
  );

  render() {
    return scheduleModel.isMultiSelect ? this.multiSelectView : this.singleSelectView;
  }
}

export default withStyles<IInfoPanelJobViewProps, unknown>(styles, InfoPanelJobView);
